import { useState } from "react";
import { Smile, X } from "lucide-react";
import { COMPOSE_EMOJI_CATEGORIES } from "@/lib/compose/composeEmojiCatalog";
import { cn } from "@/lib/cn";

type ModalProps = {
  onPick: (emoji: string) => void;
  onClose: () => void;
  className?: string;
};

/** Modale flottante — ancrée dans la zone de rédaction (overlay + carte). */
export function ComposeEmojiModal({ onPick, onClose, className }: ModalProps) {
  const [activeCategory, setActiveCategory] = useState(
    COMPOSE_EMOJI_CATEGORIES[0]?.id ?? "smileys"
  );

  const category =
    COMPOSE_EMOJI_CATEGORIES.find((item) => item.id === activeCategory) ??
    COMPOSE_EMOJI_CATEGORIES[0];

  return (
    <div
      className={cn("mail-compose-emoji-modal", className)}
      role="dialog"
      aria-label="Bibliothèque d’emojis"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="mail-compose-emoji-modal__header">
        <p className="mail-compose-emoji-modal__title">Emojis</p>
        <button
          type="button"
          aria-label="Fermer"
          title="Fermer"
          className="mail-compose-emoji-modal__close"
          onMouseDown={(event) => event.preventDefault()}
          onClick={onClose}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      <div className="mail-compose-emoji-modal__tabs" role="tablist" aria-label="Catégories">
        {COMPOSE_EMOJI_CATEGORIES.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={item.id === activeCategory}
            title={item.label}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setActiveCategory(item.id)}
            className={cn(
              "mail-compose-emoji-modal__tab",
              item.id === activeCategory && "mail-compose-emoji-modal__tab--active"
            )}
          >
            {item.emojis[0]}
          </button>
        ))}
      </div>

      <p className="mail-compose-emoji-modal__heading">{category.label}</p>

      <div className="mail-compose-emoji-modal__grid" role="list">
        {category.emojis.map((emoji) => (
          <button
            key={`${category.id}-${emoji}`}
            type="button"
            role="listitem"
            title={emoji}
            aria-label={`Insérer ${emoji}`}
            className="mail-compose-emoji-modal__emoji"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onPick(emoji)}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}

type ToggleProps = {
  open: boolean;
  onToggle: () => void;
  className?: string;
};

export function ComposeEmojiToggle({ open, onToggle, className }: ToggleProps) {
  return (
    <button
      type="button"
      title="Insérer un emoji"
      aria-label="Insérer un emoji"
      aria-expanded={open}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onToggle}
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]",
        open && "bg-[var(--mail-surface-hover)] text-[var(--mail-accent)]",
        className
      )}
    >
      <Smile className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}
